import { MapPin, Phone, Navigation, Clock } from "lucide-react";
import { toast } from "sonner";
import SectionHeading, { Reveal } from "@/components/SectionHeading";
import { BRANCHES, CONTACT, OPENING_HOURS } from "@/data/content";

const StatusBadge = ({ status }) =>
  status === "OPEN" ? (
    <span
      data-testid="branch-status-open"
      className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
    >
      Open
    </span>
  ) : (
    <span
      data-testid="branch-status-opening-soon"
      className="bg-[#FF5A1F]/20 text-[#FF5A1F] border border-[#FF5A1F]/60 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest animate-pulse"
    >
      Opening Soon
    </span>
  );

const HoursLine = () => (
  <p data-testid="branch-opening-hours" className="mt-2 flex items-start gap-2 text-sm leading-relaxed text-[#D1D1D1]">
    <Clock className="w-4 h-4 text-[#FF5A1F] mt-0.5 shrink-0" />
    <span>
      {OPENING_HOURS.days}: {OPENING_HOURS.morning} &amp; {OPENING_HOURS.evening}
      <br />
      {OPENING_HOURS.sunday}
    </span>
  </p>
);

export default function Branches() {
  return (
    <section id="branches" data-testid="branches-section" className="bg-[#1A1A1A] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Our Branches"
          title="Find Your"
          accent="F12"
          subtext="Two branches are open and training today in Thiruvananthapuram. F12 FITNESS Chapter 3 is opening soon."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {BRANCHES.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.1} className="h-full">
              <article
                data-testid={`branch-card-${b.id}`}
                className="group bg-[#141414] border border-white/10 rounded-xl overflow-hidden h-full flex flex-col transition-all duration-300 hover:border-[#FF5A1F]/60 hover:shadow-[0_16px_40px_rgba(255,90,31,0.15)] hover:-translate-y-1.5"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={b.image}
                    alt={b.photoAlt}
                    loading="lazy"
                    className={`w-full aspect-[16/10] object-cover transition-transform duration-500 group-hover:scale-105 ${
                      b.status === "OPENING SOON" ? "grayscale-[0.4]" : ""
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-transparent to-transparent" />
                  <div className="absolute top-4 left-4">
                    <StatusBadge status={b.status} />
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-heading text-2xl tracking-wider text-white uppercase">
                    {b.name}
                  </h3>
                  {b.area && (
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] font-semibold text-[#FF5A1F]">
                      {b.area}
                    </p>
                  )}
                  {b.status === "OPEN" ? (
                    <>
                      <p className="mt-3 flex items-start gap-2 text-sm leading-relaxed text-[#D1D1D1]">
                        <MapPin className="w-4 h-4 text-[#FF5A1F] mt-0.5 shrink-0" />
                        {b.address}
                      </p>
                      <div className="flex-1">
                        <HoursLine />
                      </div>
                      <div className="mt-6 flex gap-3">
                        <a
                          data-testid={`branch-call-${b.id}-button`}
                          href={CONTACT.phoneHref}
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF5A1F] hover:bg-[#E0480F] text-white font-heading text-lg tracking-widest px-4 py-2.5 rounded-sm transition-colors"
                        >
                          <Phone className="w-4 h-4" /> CALL NOW
                        </a>
                        <a
                          data-testid={`branch-directions-${b.id}-button`}
                          href={b.mapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-[#FF5A1F] text-[#FF5A1F] hover:bg-[#FF5A1F] hover:text-white font-heading text-lg tracking-widest px-4 py-2.5 rounded-sm transition-colors"
                        >
                          <Navigation className="w-4 h-4" /> GET DIRECTIONS
                        </a>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="mt-3 text-sm leading-relaxed text-[#D1D1D1] flex-1">
                        Address and contact details will be announced soon. Stay tuned for our
                        grand opening.
                      </p>
                      <button
                        data-testid="branch-stay-tuned-button"
                        onClick={() =>
                          toast.info("Chapter 3 details coming soon — stay tuned!")
                        }
                        className="mt-6 inline-flex items-center justify-center gap-2 bg-[#FF5A1F] hover:bg-[#E0480F] text-white font-heading text-lg tracking-widest px-6 py-2.5 rounded-sm transition-colors"
                      >
                        STAY TUNED
                      </button>
                    </>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
